import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import {
  addEdge,
  Background,
  Connection,
  Edge,
  Node,
  ReactFlow,
  ReactFlowInstance,
  ReactFlowProvider,
  useReactFlow
} from '@xyflow/react';
import { Spin } from 'antd';

import { flowChatTypes } from 'modules/ai-agent/constants';
import { generateRequestBodyFromNodes, getInvalidNodeIds } from 'modules/ai-agent/helpers';
import { useGetAIAgent, useSyncFlowChanges } from 'modules/ai-agent/hooks';
import {
  setActiveBlockId,
  setEdges,
  setIsUpdate,
  setLlmBlocks,
  setNodes,
  setOpenAIDrawer
} from 'modules/ai-agent/store';

import ChatSimulationDrawer from './chat-simulation/chat-simulation-drawer';
import Controllers from './react-flow/controllers';
import CustomEdge from './react-flow/edge-delete-dropdown';
import FlowDrawer from './react-flow/flaw-drawer/flow-drawer';
import FlowHeader from './react-flow/flow-header';
import { nodeTypes } from './react-flow/node-types/node-types';

import '@xyflow/react/dist/style.css';

const LayoutFlow = () => {
  const dispatch = useAppDispatch();
  const nodes = useAppSelector(state => state.aiAgentReducer.nodes);
  const initialNodes = useAppSelector(state => state.aiAgentReducer.initialNodes);
  const edges = useAppSelector(state => state.aiAgentReducer.edges);
  const messages = useAppSelector(state => state.aiAgentReducer.messages);
  const isSubmitted = useAppSelector(state => state.aiAgentReducer.isSubmitted);
  const [history, setHistory] = useState<{ nodes: Node[]; edges: Edge[] }[]>([]);
  const [future, setFuture] = useState<{ nodes: Node[]; edges: Edge[] }[]>([]);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const { isLoading, isFetching } = useGetAIAgent();
  const hasFitViewRun = useRef(false);

  const { onEdgesChange, onNodesChange } = useSyncFlowChanges();
  const { fitView } = useReactFlow();

  const onOpenDrawer = (nodeType: keyof typeof flowChatTypes, nodeId: string) => {
    dispatch(setOpenAIDrawer({ open: true, nodeType, nodeId }));
  };

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      dispatch(setEdges(prev => addEdge(params, prev as any)));
      dispatch(setIsUpdate(true));
    },
    [dispatch]
  );

  const edgeTypes = useMemo(
    () => ({
      buttonEdge: (props: any) => <CustomEdge {...props} />
    }),
    []
  );

  const lastSimulatedBlockId = useMemo(() => {
    const lastMessage = messages[messages.length - 1];

    if (!lastMessage || !lastMessage.simulateAnswer?.length) return null;

    const lastAnswer = lastMessage.simulateAnswer[lastMessage.simulateAnswer.length - 1];

    return lastAnswer?.blockAnswer?.block?.id ?? null;
  }, [messages]);

  const newNodes = generateRequestBodyFromNodes();
  const invalidNodeIds = isSubmitted ? getInvalidNodeIds(newNodes as any) : [];

  const styledNodes = (nodes as (Node & { oldId: string })[]).map(node => ({
    ...node,
    style: {
      ...(node.style || {}),
      border:
        isSubmitted && invalidNodeIds.includes(node.id || node?.oldId) ? '2px solid #F04438' : '2px solid transparent',
      borderRadius: '12px'
    }
  }));

  useEffect(() => {
    const filteredLlmBlocks = nodes?.filter(item => item?.type === flowChatTypes.LLM) || [];

    const llmBlocks = (filteredLlmBlocks as (Node & { oldId: string })[])?.map(item => ({
      name: item?.data?.name as string,
      key: `#${item?.id || item?.oldId}#`
    }));

    dispatch(setLlmBlocks(llmBlocks));
  }, [nodes]);

  useEffect(() => {
    if (!hasFitViewRun.current && initialNodes.length > 1) {
      hasFitViewRun.current = true;
      setTimeout(() => {
        fitView({ padding: 0.1, maxZoom: 0.8, duration: 500 });
      }, 100);
    }
  }, [initialNodes]);

  useEffect(() => {
    if (!lastSimulatedBlockId) return;

    const node = nodes?.find(item => item?.id === String(lastSimulatedBlockId));

    if (node) {
      dispatch(setActiveBlockId(node?.id));

      rfInstance?.fitView({
        nodes: [node],
        duration: 500,
        padding: 0.5,
        maxZoom: 0.9
      });
    }
  }, [lastSimulatedBlockId]);

  return (
    <Spin className="!max-h-[calc(100vh-72px)]" spinning={isLoading || isFetching}>
      <div className="h-[calc(100vh-72px)] p-4">
        <div className="relative h-full w-full bg-white">
          <FlowHeader />
          <FlowDrawer />
          <ChatSimulationDrawer />
          <ReactFlow
            nodes={styledNodes}
            edges={edges?.map(item => ({ ...item, type: 'buttonEdge' }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => {
              onOpenDrawer(node?.type as keyof typeof flowChatTypes, node?.id);
            }}
            onPaneClick={() => {
              setNodes(nds => nds.map(node => ({ ...node, selected: false })));
              setEdges(eds => eds.map(edge => ({ ...edge, selected: false })));
            }}
            attributionPosition={undefined}
            defaultViewport={{ zoom: 1, x: 0, y: 0 }}
            minZoom={0.2}
            maxZoom={1.5}
            nodeTypes={nodeTypes}
            className="!bg-gray-50"
            edgeTypes={edgeTypes}
            onInit={instance => setRfInstance(instance as any)}
          >
            <Background />
            <Controllers history={history} setHistory={setHistory} setFuture={setFuture} future={future} />
          </ReactFlow>
        </div>
      </div>
    </Spin>
  );
};

export default () => (
  <ReactFlowProvider>
    <LayoutFlow />
  </ReactFlowProvider>
);
